/**
 * Admin — Landing Page Content Editor
 *
 * CRUD interface for managing the public landing page content:
 *   - Quotes (text + author, i18n keys)
 *   - Partners (name + logo URL + website, with image upload)
 *   - Team members (name + position + image URL + comment, with image upload)
 *   - Rollout blocks (heading + text, i18n keys + accent color)
 *   - Media & images (hero bg, mission, rollout, video — with uploads)
 *
 * Image uploads use unique sequential naming:
 *   Team:     SkipDavis.001.jpg, SkipDavis.002.jpg, ...
 *   Partners: UNICEF.001.png, UNICEF.002.png, ...
 *   Media:    Original filename preserved.
 *
 * Saves content JSON to /api/admin/landing-content (flat-file).
 * Images uploaded to CRA public/content/ via /api/admin/upload-landing-image.
 * English only — system admin UI.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import CloseButton from '../../Components/CloseButton/CloseButton';
import { DEFAULT_LANDING_CONTENT } from '../../Data/Landing/landingContent';
import type {
  LandingContent,
  LandingQuote,
  LandingPartner,
  LandingTeamMember,
  LandingRolloutBlock,
} from '../../Data/Landing/landingContent';
import { fetchLandingContent, saveLandingContent, uploadLandingImage } from '../../api/admin';
import { mediaSrc } from '../../api/mediaUrl';
import type { UploadCategory } from '../../api/admin';
import styles from './AdminPages.module.css';

// ── Helpers ──────────────────────────────────────────────────

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// ── Upload Hook ──────────────────────────────────────────────

interface UploadState {
  uploading: boolean;
  error: string | null;
  success: string | null;
}

function useFileUpload() {
  const [uploads, setUploads] = useState<Record<string, UploadState>>({});

  const upload = useCallback(
    async (
      key: string,
      file: File,
      category: UploadCategory,
      label?: string,
    ): Promise<string | null> => {
      setUploads((prev) => ({
        ...prev,
        [key]: { uploading: true, error: null, success: null },
      }));

      try {
        const path = await uploadLandingImage(file, category, label);
        setUploads((prev) => ({
          ...prev,
          [key]: { uploading: false, error: null, success: `Uploaded` },
        }));
        // Clear success message after 3s
        setTimeout(() => {
          setUploads((prev) => {
            const cur = prev[key];
            if (cur?.success) return { ...prev, [key]: { ...cur, success: null } };
            return prev;
          });
        }, 3000);
        return path;
      } catch (err: any) {
        setUploads((prev) => ({
          ...prev,
          [key]: { uploading: false, error: err.message || 'Upload failed', success: null },
        }));
        return null;
      }
    },
    [],
  );

  return { uploads, upload };
}

// ── File Input Trigger ───────────────────────────────────────

interface FileUploadButtonProps {
  accept?: string;
  uploadState?: UploadState;
  onFileSelected: (file: File) => void;
  disabled?: boolean;
  label?: string;
}

function FileUploadButton({
  accept = 'image/*',
  uploadState,
  onFileSelected,
  disabled,
  label = 'Choose File',
}: FileUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelected(file);
      // Reset so the same file can be re-selected
      e.target.value = '';
    }
  };

  return (
    <div className={styles.uploadRow}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        className={styles.btnUpload}
        onClick={handleClick}
        disabled={disabled || uploadState?.uploading}
      >
        {uploadState?.uploading ? 'Uploading...' : label}
      </button>
      {uploadState?.success && (
        <span className={styles.uploadStatus}>{uploadState.success}</span>
      )}
      {uploadState?.error && (
        <span className={styles.uploadError}>{uploadState.error}</span>
      )}
    </div>
  );
}

// ── Thumbnail Preview ────────────────────────────────────────

function ImageThumb({ src }: { src: string }) {
  if (!src) return null;
  const url = mediaSrc(src) ?? undefined;
  return (
    <img
      src={url}
      alt=""
      className={styles.imagePreview}
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

// ── Main Component ───────────────────────────────────────────

export default function AdminLandingContent() {
  const [content, setContent] = useState<LandingContent>(deepClone(DEFAULT_LANDING_CONTENT));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const { uploads, upload } = useFileUpload();

  // ── Load ──
  useEffect(() => {
    (async () => {
      try {
        const saved = await fetchLandingContent();
        if (saved) setContent(saved);
      } catch {
        // Use defaults — API may not be running
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Save ──
  const handleSave = useCallback(async () => {
    setSaving(true);
    setStatus(null);
    try {
      await saveLandingContent(content);
      setDirty(false);
      setStatus({ type: 'success', msg: 'Saved successfully' });
    } catch (err: any) {
      setStatus({ type: 'error', msg: err.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  }, [content]);

  // ── Mutation helpers ──
  const update = useCallback((patch: Partial<LandingContent>) => {
    setContent((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setStatus(null);
  }, []);

  // ── Quote CRUD ──
  const addQuote = () => {
    const idx = content.quotes.length + 1;
    update({
      quotes: [
        ...content.quotes,
        {
          text: '',
          author: '',
          textKey: `hero.quotes.${idx}.text`,
          authorKey: `hero.quotes.${idx}.author`,
        },
      ],
    });
  };

  const updateQuote = (i: number, patch: Partial<LandingQuote>) => {
    const quotes = [...content.quotes];
    quotes[i] = { ...quotes[i], ...patch };
    update({ quotes });
  };

  const removeQuote = (i: number) => {
    update({ quotes: content.quotes.filter((_, idx) => idx !== i) });
  };

  // ── Partner CRUD ──
  const addPartner = () => {
    update({
      partners: [...content.partners, { name: '', logoUrl: '', websiteUrl: '' }],
    });
  };

  const updatePartner = (i: number, patch: Partial<LandingPartner>) => {
    const partners = [...content.partners];
    partners[i] = { ...partners[i], ...patch };
    update({ partners });
  };

  const removePartner = (i: number) => {
    update({ partners: content.partners.filter((_, idx) => idx !== i) });
  };

  const handlePartnerUpload = async (i: number, file: File) => {
    const partner = content.partners[i];
    const label = partner.name || `Partner${i + 1}`;
    const path = await upload(`partner-${i}`, file, 'partners', label);
    if (path) {
      updatePartner(i, { logoUrl: path });
    }
  };

  // ── Team CRUD ──
  const addTeamMember = () => {
    update({
      team: [...content.team, { name: '', position: '', imageUrl: '', comment: '' }],
    });
  };

  const updateTeamMember = (i: number, patch: Partial<LandingTeamMember>) => {
    const team = [...content.team];
    team[i] = { ...team[i], ...patch };
    update({ team });
  };

  const removeTeamMember = (i: number) => {
    update({ team: content.team.filter((_, idx) => idx !== i) });
  };

  const handleTeamUpload = async (i: number, file: File) => {
    const member = content.team[i];
    const label = member.name || `TeamMember${i + 1}`;
    const path = await upload(`team-${i}`, file, 'team', label);
    if (path) {
      updateTeamMember(i, { imageUrl: path });
    }
  };

  // ── Media uploads ──
  const handleMediaUpload = async (
    field: 'heroBgImage' | 'missionImage' | 'rolloutImage',
    file: File,
  ) => {
    const path = await upload(`media-${field}`, file, 'media');
    if (path) {
      update({ [field]: path });
    }
  };

  const handleVideoUpload = async (file: File) => {
    const path = await upload('media-video', file, 'media');
    if (path) {
      // Extract the base name from the returned path:
      // e.g. "/content/video/my_video.mp4" → "my_video"
      const fileName = path.split('/').pop() || '';
      const baseName = fileName.replace(/\.[^.]+$/, '');
      update({ videoBaseName: baseName });
    }
  };

  // ── Rollout CRUD ──
  const updateRollout = (i: number, patch: Partial<LandingRolloutBlock>) => {
    const rolloutBlocks = [...content.rolloutBlocks];
    rolloutBlocks[i] = { ...rolloutBlocks[i], ...patch };
    update({ rolloutBlocks });
  };

  // ── Reset to defaults ──
  const handleReset = () => {
    setContent(deepClone(DEFAULT_LANDING_CONTENT));
    setDirty(true);
    setStatus(null);
  };

  if (loading) {
    return (
      <div>
        <h1 className={styles.pageTitle}>Landing Page Content</h1>
        <p className={styles.pageSubtitle}>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Landing Page Content</h1>
          <p className={styles.pageSubtitle}>
            Edit the public landing page content. Changes are saved to the server.
          </p>
        </div>
        <div className={styles.pageActions}>
          <button
            className={styles.btnSecondary}
            onClick={handleReset}
          >
            Reset to Defaults
          </button>
          <button
            className={`${styles.btnPrimary} ${dirty ? styles.btnDirty : ''}`}
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      {status && (
        <div className={`${styles.statusBar} ${styles[`status_${status.type}`]}`}>
          {status.msg}
        </div>
      )}

      {/* ── QUOTES ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Hero Quotes</h2>
          <button className={styles.btnAdd} onClick={addQuote}>+ Add Quote</button>
        </div>
        <p className={styles.sectionHint}>
          Enter the quote text and author directly. If left blank, the system falls
          back to the i18n key (for multilingual support).
        </p>

        {content.quotes.map((q, i) => (
          <div key={i} className={styles.editRow}>
            <div className={styles.editRowNum}>{i + 1}</div>
            <div className={styles.editRowFields}>
              <label className={styles.fieldLabel}>
                Quote text
                <input
                  className={styles.input}
                  value={q.text || ''}
                  onChange={(e) => updateQuote(i, { text: e.target.value })}
                  placeholder="Enter the quote text"
                />
              </label>
              <label className={styles.fieldLabel}>
                Author
                <input
                  className={styles.input}
                  value={q.author || ''}
                  onChange={(e) => updateQuote(i, { author: e.target.value })}
                  placeholder="Attribution"
                />
              </label>
              <label className={styles.fieldLabel}>
                i18n text key
                <input
                  className={styles.input}
                  value={q.textKey}
                  onChange={(e) => updateQuote(i, { textKey: e.target.value })}
                  style={{ opacity: q.text ? 0.5 : 1 }}
                />
                <span className={styles.fieldHint}>
                  Fallback — only used if quote text above is empty
                </span>
              </label>
              <label className={styles.fieldLabel}>
                i18n author key
                <input
                  className={styles.input}
                  value={q.authorKey}
                  onChange={(e) => updateQuote(i, { authorKey: e.target.value })}
                  style={{ opacity: q.author ? 0.5 : 1 }}
                />
                <span className={styles.fieldHint}>
                  Fallback — only used if author above is empty
                </span>
              </label>
            </div>
            <CloseButton onClick={() => removeQuote(i)} size="sm" variant="danger" title="Remove" />
          </div>
        ))}
      </section>

      {/* ── PARTNERS ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Partners</h2>
          <button className={styles.btnAdd} onClick={addPartner}>+ Add Partner</button>
        </div>
        <p className={styles.sectionHint}>
          Upload a logo image or enter a URL manually. Uploaded files are named
          automatically (e.g. UNICEF.001.png, UNICEF.002.png).
        </p>

        {content.partners.length === 0 && (
          <p className={styles.emptyMsg}>No partners configured. The section will be hidden on the landing page.</p>
        )}

        {content.partners.map((p, i) => (
          <div key={i} className={styles.editRow}>
            <div className={styles.editRowNum}>{i + 1}</div>
            {p.logoUrl && <ImageThumb src={p.logoUrl} />}
            <div className={styles.editRowFields}>
              <label className={styles.fieldLabel}>
                Name
                <input
                  className={styles.input}
                  value={p.name}
                  onChange={(e) => updatePartner(i, { name: e.target.value })}
                  placeholder="Partner name"
                />
              </label>
              <label className={styles.fieldLabel}>
                Logo URL
                <input
                  className={styles.input}
                  value={p.logoUrl}
                  onChange={(e) => updatePartner(i, { logoUrl: e.target.value })}
                  placeholder="/content/images/partners/logo.png"
                />
                <FileUploadButton
                  onFileSelected={(file) => handlePartnerUpload(i, file)}
                  uploadState={uploads[`partner-${i}`]}
                  label="Upload Logo"
                />
              </label>
              <label className={styles.fieldLabel}>
                Website
                <input
                  className={styles.input}
                  value={p.websiteUrl || ''}
                  onChange={(e) => updatePartner(i, { websiteUrl: e.target.value })}
                  placeholder="https://..."
                />
              </label>
            </div>
            <CloseButton onClick={() => removePartner(i)} size="sm" variant="danger" title="Remove" />
          </div>
        ))}
      </section>

      {/* ── TEAM ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Team Members</h2>
          <button className={styles.btnAdd} onClick={addTeamMember}>+ Add Member</button>
        </div>
        <p className={styles.sectionHint}>
          Upload a photo or enter a URL. Photos are named based on the member's name
          (e.g. SkipDavis.001.jpg). Enter the name first, then upload.
        </p>

        {content.team.length === 0 && (
          <p className={styles.emptyMsg}>No team members configured. The section will be hidden on the landing page.</p>
        )}

        {content.team.map((m, i) => (
          <div key={i} className={styles.editRow}>
            <div className={styles.editRowNum}>{i + 1}</div>
            {m.imageUrl && <ImageThumb src={m.imageUrl} />}
            <div className={styles.editRowFields}>
              <label className={styles.fieldLabel}>
                Name
                <input
                  className={styles.input}
                  value={m.name}
                  onChange={(e) => updateTeamMember(i, { name: e.target.value })}
                  placeholder="Full name"
                />
              </label>
              <label className={styles.fieldLabel}>
                Position
                <input
                  className={styles.input}
                  value={m.position}
                  onChange={(e) => updateTeamMember(i, { position: e.target.value })}
                  placeholder="Role / title"
                />
              </label>
              <label className={styles.fieldLabel}>
                Image URL
                <input
                  className={styles.input}
                  value={m.imageUrl}
                  onChange={(e) => updateTeamMember(i, { imageUrl: e.target.value })}
                  placeholder="/content/images/team/name.jpg"
                />
                <FileUploadButton
                  onFileSelected={(file) => handleTeamUpload(i, file)}
                  uploadState={uploads[`team-${i}`]}
                  label="Upload Photo"
                />
              </label>
              <label className={styles.fieldLabel}>
                Comment
                <input
                  className={styles.input}
                  value={m.comment || ''}
                  onChange={(e) => updateTeamMember(i, { comment: e.target.value })}
                  placeholder="Optional short bio"
                />
              </label>
            </div>
            <CloseButton onClick={() => removeTeamMember(i)} size="sm" variant="danger" title="Remove" />
          </div>
        ))}
      </section>

      {/* ── ROLLOUT BLOCKS ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Rollout Blocks</h2>
        </div>
        <p className={styles.sectionHint}>
          Four pillar feature blocks. Enter heading and description directly.
          i18n keys are used as fallback for multilingual support.
        </p>

        {content.rolloutBlocks.map((b, i) => (
          <div key={i} className={styles.editRow}>
            <div className={styles.editRowNum}>{i + 1}</div>
            <div className={styles.editRowFields}>
              <label className={styles.fieldLabel}>
                Heading
                <input
                  className={styles.input}
                  value={b.heading || ''}
                  onChange={(e) => updateRollout(i, { heading: e.target.value })}
                  placeholder="Block heading"
                />
              </label>
              <label className={styles.fieldLabel}>
                Description
                <input
                  className={styles.input}
                  value={b.text || ''}
                  onChange={(e) => updateRollout(i, { text: e.target.value })}
                  placeholder="Block description"
                />
              </label>
              <label className={styles.fieldLabel}>
                Color
                <div className={styles.colorField}>
                  <input
                    type="color"
                    value={b.color}
                    onChange={(e) => updateRollout(i, { color: e.target.value })}
                    className={styles.colorPicker}
                  />
                  <input
                    className={styles.input}
                    value={b.color}
                    onChange={(e) => updateRollout(i, { color: e.target.value })}
                    style={{ flex: 1 }}
                  />
                </div>
              </label>
              <label className={styles.fieldLabel}>
                i18n heading key
                <input
                  className={styles.input}
                  value={b.headingKey}
                  onChange={(e) => updateRollout(i, { headingKey: e.target.value })}
                  style={{ opacity: b.heading ? 0.5 : 1 }}
                />
                <span className={styles.fieldHint}>Fallback for multilingual</span>
              </label>
              <label className={styles.fieldLabel}>
                i18n text key
                <input
                  className={styles.input}
                  value={b.textKey}
                  onChange={(e) => updateRollout(i, { textKey: e.target.value })}
                  style={{ opacity: b.text ? 0.5 : 1 }}
                />
                <span className={styles.fieldHint}>Fallback for multilingual</span>
              </label>
            </div>
          </div>
        ))}
      </section>

      {/* ── MEDIA PATHS ── */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Media &amp; Images</h2>
        </div>
        <p className={styles.sectionHint}>
          Upload files or enter paths manually. Uploaded media goes to the
          public content directory and the path auto-fills below.
        </p>

        <div className={styles.fieldGrid}>
          <label className={styles.fieldLabel}>
            Video base name
            <input
              className={styles.input}
              value={content.videoBaseName}
              onChange={(e) => update({ videoBaseName: e.target.value })}
              placeholder="landing_v1"
            />
            <span className={styles.fieldHint}>
              Expects: /content/video/{'{name}'}.mp4, _540.mp4, _270.mp4
            </span>
            <FileUploadButton
              accept="video/mp4,video/webm,video/quicktime"
              onFileSelected={(file) => handleVideoUpload(file)}
              uploadState={uploads['media-video']}
              label="Upload Video"
            />
          </label>

          <label className={styles.fieldLabel}>
            Hero background image
            <input
              className={styles.input}
              value={content.heroBgImage}
              onChange={(e) => update({ heroBgImage: e.target.value })}
            />
            <FileUploadButton
              onFileSelected={(file) => handleMediaUpload('heroBgImage', file)}
              uploadState={uploads['media-heroBgImage']}
              label="Upload Image"
            />
          </label>

          <label className={styles.fieldLabel}>
            Mission section image
            <input
              className={styles.input}
              value={content.missionImage}
              onChange={(e) => update({ missionImage: e.target.value })}
            />
            <FileUploadButton
              onFileSelected={(file) => handleMediaUpload('missionImage', file)}
              uploadState={uploads['media-missionImage']}
              label="Upload Image"
            />
          </label>

          <label className={styles.fieldLabel}>
            Rollout section image
            <input
              className={styles.input}
              value={content.rolloutImage}
              onChange={(e) => update({ rolloutImage: e.target.value })}
            />
            <FileUploadButton
              onFileSelected={(file) => handleMediaUpload('rolloutImage', file)}
              uploadState={uploads['media-rolloutImage']}
              label="Upload Image"
            />
          </label>
        </div>
      </section>
    </div>
  );
}
